import React, { useState, useEffect, useRef, useCallback } from 'react';
import { findFuzzyMatches } from '../utils/fuzzyMatch';
import { C } from '../styles/theme';
import { parseCsv, downloadCsvContent } from './bulkImport/csvUtils';

// ─── Brand tokens ─────────────────────────────────────────────────────────────
const FONT = "'Plus Jakarta Sans', sans-serif";

// ─── CSV helpers ──────────────────────────────────────────────────────────────
// Parsing and download live in bulkImport/csvUtils so RecipeImportModal reads
// files identically. Header keys stay snake_cased here — every column config
// below is written against those keys.
const parseCSV = (text) =>
    parseCsv(text, { normaliseHeaders: true }).rows;

const generateCSV = (columns, exampleRows) => {
    const header = columns.map(c => c.label).join(',');
    const rows = (exampleRows || []).map(row =>
        columns.map(c => {
            const val = row[c.key] || '';
            return String(val).includes(',') ? `"${val}"` : val;
        }).join(',')
    );
    return [header, ...rows].join('\n');
};

const downloadCSV = downloadCsvContent;

// ─── Inline dropdown with create ──────────────────────────────────────────────
const InlineDropdown = ({ value, options, onChange, allowCreate, onCreateOption, placeholder, t, rowData }) => {
    const [open, setOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [newVal, setNewVal] = useState('');
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handler = e => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setCreating(false); } };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const resolvedOptions = typeof options === 'function' ? [] : (options || []);

    const handleCreate = async () => {
        if (!newVal.trim()) return;
        if (onCreateOption) {
            const created = await onCreateOption(newVal.trim(), rowData);
            if (created) onChange(created);
        }
        setNewVal('');
        setCreating(false);
        setOpen(false);
    };

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button type="button" onClick={() => setOpen(o => !o)}
                style={{
                    width: '100%', padding: '5px 8px', border: `1px solid ${C.border}`, borderRadius: 4,
                    fontSize: 12, background: C.white, color: value ? C.text : C.mutedLight,
                    textAlign: 'left', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    boxSizing: 'border-box',
                }}>
                {value || placeholder || '—'}
                <span style={{ float: 'right', color: C.mutedLight, fontSize: 9 }}>&#9660;</span>
            </button>
            {open && (
                <div style={{
                    position: 'absolute', top: '100%', left: 0, minWidth: '100%', marginTop: 2,
                    background: C.white, border: `1px solid ${C.border}`, borderRadius: 6,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 100,
                    overflow: 'hidden',
                }}>
                    <div style={{ maxHeight: 160, overflowY: 'auto' }}>
                        {resolvedOptions.map((opt, i) => (
                            <div key={i} onClick={() => { onChange(opt); setOpen(false); }}
                                style={{
                                    padding: '6px 10px', fontSize: 12, cursor: 'pointer', color: C.text,
                                    background: opt === value ? C.greenLight : C.white, whiteSpace: 'nowrap',
                                }}
                                onMouseEnter={e => { if (opt !== value) e.currentTarget.style.background = C.bg; }}
                                onMouseLeave={e => { if (opt !== value) e.currentTarget.style.background = C.white; }}>
                                {opt}
                            </div>
                        ))}
                        {resolvedOptions.length === 0 && !allowCreate && (
                            <div style={{ padding: '8px 10px', fontSize: 12, color: C.mutedLight, fontStyle: 'italic' }}>
                                {t('no_options')}
                            </div>
                        )}
                    </div>
                    {allowCreate && !creating && (
                        <div onClick={() => setCreating(true)}
                            style={{
                                padding: '6px 10px', fontSize: 12, cursor: 'pointer', color: C.green,
                                fontWeight: 600, borderTop: `1px solid ${C.border}`, background: C.white,
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = C.greenLight}
                            onMouseLeave={e => e.currentTarget.style.background = C.white}>
                            + {t('add_new')}
                        </div>
                    )}
                    {creating && (
                        <div style={{ padding: '8px 10px', borderTop: `1px solid ${C.border}`, background: C.bg }}>
                            <div style={{ display: 'flex', gap: 6 }}>
                                <input value={newVal} onChange={e => setNewVal(e.target.value)} autoFocus
                                    placeholder={t('enter_name') || 'Enter name...'}
                                    onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') { setCreating(false); setNewVal(''); } }}
                                    style={{ flex: 1, padding: '5px 8px', border: `1.5px solid ${C.green}`, borderRadius: 5, fontSize: 12, outline: 'none' }} />
                                <button onClick={handleCreate} disabled={!newVal.trim()}
                                    style={{ padding: '5px 10px', border: 'none', borderRadius: 5, background: C.green, color: C.white, fontSize: 11, fontWeight: 600, cursor: 'pointer', opacity: newVal.trim() ? 1 : 0.5 }}>
                                    {t('create')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ─── Row status helpers ───────────────────────────────────────────────────────
const STATUS_READY = 'ready';
const STATUS_REVIEW = 'review';
const STATUS_ERROR = 'error';

const statusColor = (status) => {
    if (status === STATUS_READY) return { bg: '#eff5ff', fg: '#16a34a', border: '#86efac' };
    if (status === STATUS_REVIEW) return { bg: C.amberLight, fg: C.amber, border: '#fbbf24' };
    return { bg: C.redLight, fg: C.red, border: '#fca5a5' };
};

const validateRow = (row, columns, existingNames) => {
    const errors = [];
    const fuzzyMatches = [];
    columns.forEach(col => {
        if (col.required && !row[col.key] && row[col.key] !== 0) {
            errors.push(col.key);
        }
    });
    if (row.name && existingNames.length > 0) {
        const exact = existingNames.some(n => n.toLowerCase().trim() === row.name.toLowerCase().trim());
        if (exact) {
            errors.push('name_duplicate');
        } else {
            const matches = findFuzzyMatches(row.name, existingNames);
            if (matches.length > 0) {
                fuzzyMatches.push(...matches);
            }
        }
    }
    if (errors.length > 0) return { status: STATUS_ERROR, errors, fuzzyMatches: [] };
    if (fuzzyMatches.length > 0) return { status: STATUS_REVIEW, errors: [], fuzzyMatches };
    return { status: STATUS_READY, errors: [], fuzzyMatches: [] };
};


// ─── BulkAddModal ─────────────────────────────────────────────────────────────
const BulkAddModal = ({ open, onClose, onImport, config, t }) => {
    const [activeTab, setActiveTab] = useState('csv');
    const [rows, setRows] = useState([]);
    const [csvRows, setCsvRows] = useState([]);
    const [csvChecked, setCsvChecked] = useState({});
    const [importing, setImporting] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [touchedCells, setTouchedCells] = useState({});
    const fileRef = useRef(null);

    const { columns, emptyRow, existingNames, templateFilename, exampleRows, createItem, title, instructions, transforms } = config;

    // Reset state when modal opens
    useEffect(() => {
        if (open) {
            setActiveTab('csv');
            setCsvRows([]);
            setCsvChecked({});
            setImporting(false);
            setDragOver(false);
            setTouchedCells({});
            setRows(Array.from({ length: 5 }, () => emptyRow()));
        }
    }, [open, emptyRow]);

    // Escape key
    useEffect(() => {
        if (!open) return;
        const onKey = e => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    // ── CSV tab handlers ──────────────────────────────────────────────────────
    const handleDownloadTemplate = () => {
        const csv = generateCSV(columns, exampleRows);
        downloadCSV(csv, templateFilename || 'template.csv');
    };

    const mapCsvHeaderToKey = useCallback((header) => {
        const h = header.toLowerCase().replace(/\s+/g, '_');
        const col = columns.find(c => c.key === h || c.label.toLowerCase().replace(/\s+/g, '_') === h);
        return col ? col.key : null;
    }, [columns]);

    const processCSVFile = useCallback((file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const rawRows = parseCSV(e.target.result);
            const mapped = rawRows.map(raw => {
                const row = emptyRow();
                Object.entries(raw).forEach(([hdr, val]) => {
                    const key = mapCsvHeaderToKey(hdr);
                    if (key) row[key] = val;
                });
                return transforms ? transforms(row) : row;
            }).filter(r => r.name && r.name.trim());
            setCsvRows(mapped);
            const checks = {};
            mapped.forEach((_, i) => { checks[i] = true; });
            setCsvChecked(checks);
        };
        reader.readAsText(file);
    }, [emptyRow, mapCsvHeaderToKey, transforms]);

    const handleFileDrop = (e) => {
        e.preventDefault(); setDragOver(false);
        const file = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
        if (file && file.name.endsWith('.csv')) processCSVFile(file);
    };

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) processCSVFile(file);
        e.target.value = '';
    };

    const csvValidated = csvRows.map(row => ({ row, ...validateRow(row, columns, existingNames || []) }));
    const readyCount = csvValidated.filter(r => r.status === STATUS_READY).length;
    const reviewCount = csvValidated.filter(r => r.status === STATUS_REVIEW).length;
    const errorCount = csvValidated.filter(r => r.status === STATUS_ERROR).length;
    const selectedCount = Object.values(csvChecked).filter(Boolean).length;

    const handleImportCSV = async () => {
        const toImport = csvValidated.filter((_, i) => csvChecked[i] && csvValidated[i].status === STATUS_READY);
        if (toImport.length === 0) return;

        setImporting(true);
        let success = 0, failed = 0;
        for (const { row } of toImport) {
            try {
                await createItem(row);
                success++;
            } catch {
                failed++;
            }
        }
        setImporting(false);
        if (onImport) onImport({ success, failed });
        if (success > 0) onClose();
    };

    // ── Manual tab handlers ───────────────────────────────────────────────────
    const updateRow = (idx, key, value) => {
        setRows(prev => prev.map((r, i) => {
            if (i !== idx) return r;
            const updated = { ...r, [key]: value };
            return transforms ? transforms(updated) : updated;
        }));
    };

    const addRow = () => setRows(prev => [...prev, emptyRow()]);

    const removeRow = (idx) => {
        setRows(prev => prev.filter((_, i) => i !== idx));
    };

    const markTouched = (idx, key) => {
        setTouchedCells(prev => ({ ...prev, [`${idx}-${key}`]: true }));
    };

    const manualValidated = rows.map(row => ({ row, ...validateRow(row, columns, existingNames || []) }));
    const manualNonEmpty = rows.filter(r => columns.some(c => r[c.key] && String(r[c.key]).trim()));

    const handleAddManual = async () => {
        const nonEmpty = manualValidated.filter((v, i) =>
            columns.some(c => rows[i][c.key] && String(rows[i][c.key]).trim())
        );
        const valid = nonEmpty.filter(v => v.status === STATUS_READY);
        if (valid.length === 0) return;

        setImporting(true);
        let success = 0, failed = 0;
        for (const { row } of valid) {
            try {
                await createItem(row);
                success++;
            } catch {
                failed++;
            }
        }
        setImporting(false);
        if (onImport) onImport({ success, failed });
        if (success > 0) onClose();
    };

    if (!open) return null;

    // ── Resolve dynamic options per row ───────────────────────────────────────
    const getOptions = (col, row) => {
        if (typeof col.options === 'function') return col.options(row);
        return col.options || [];
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 1200,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: FONT,
        }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div style={{
                background: C.white, borderRadius: 12, padding: 28,
                width: '95vw', maxWidth: 900, maxHeight: '90vh',
                display: 'flex', flexDirection: 'column',
                boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            }} onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <h2 style={{ fontSize: 20, fontWeight: 800, color: C.text, margin: 0 }}>{t(title)}</h2>
                    <button onClick={onClose} style={{
                        width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.border}`,
                        background: C.white, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: C.muted, fontSize: 18,
                    }}>&times;</button>
                </div>
                <p style={{ fontSize: 13, color: C.muted, margin: '0 0 18px', lineHeight: 1.5 }}>{t(instructions)}</p>

                {/* Tabs */}
                <div style={{ display: 'flex', gap: 2, background: '#f1f5f9', borderRadius: 8, padding: 3, marginBottom: 18, alignSelf: 'flex-start' }}>
                    {[['csv', t('upload_csv')], ['manual', t('manual_entry')]].map(([key, label]) => (
                        <button key={key} onClick={() => setActiveTab(key)}
                            style={{
                                padding: '6px 18px', borderRadius: 6, border: 'none', cursor: 'pointer',
                                fontSize: 13, fontWeight: 600,
                                background: activeTab === key ? C.white : 'transparent',
                                color: activeTab === key ? C.text : C.muted,
                                boxShadow: activeTab === key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                            }}>
                            {label}
                        </button>
                    ))}
                </div>

                {/* ── CSV Tab ──────────────────────────────────────────────────────── */}
                {activeTab === 'csv' && (
                    <div style={{ flex: 1, overflow: 'auto' }}>
                        {csvRows.length === 0 ? (
                            <>
                                {/* Download template */}
                                <button onClick={handleDownloadTemplate}
                                    style={{
                                        padding: '8px 16px', borderRadius: 8, border: `1px solid ${C.green}`,
                                        background: C.white, color: C.green, fontSize: 13, fontWeight: 600,
                                        cursor: 'pointer', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6,
                                    }}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                                    </svg>
                                    {t('download_template')}
                                </button>

                                {/* Drop zone */}
                                <div
                                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                                    onDragLeave={() => setDragOver(false)}
                                    onDrop={handleFileDrop}
                                    onClick={() => fileRef.current?.click()}
                                    style={{
                                        border: `2px dashed ${dragOver ? C.green : C.border}`,
                                        borderRadius: 12, padding: '48px 20px', textAlign: 'center',
                                        cursor: 'pointer', background: dragOver ? C.greenLight : C.bg,
                                        transition: 'all 0.15s',
                                    }}>
                                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={dragOver ? C.green : C.mutedLight} strokeWidth="1.5" style={{ marginBottom: 12 }}>
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                                    </svg>
                                    <div style={{ fontSize: 14, color: C.muted, fontWeight: 500 }}>{t('drag_or_click_upload')}</div>
                                    <div style={{ fontSize: 12, color: C.mutedLight, marginTop: 4 }}>CSV (.csv)</div>
                                    <input ref={fileRef} type="file" accept=".csv" onChange={handleFileSelect} style={{ display: 'none' }} />
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Summary bar */}
                                <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <span style={{ fontSize: 12, fontWeight: 600, color: '#16a34a', background: '#eff5ff', padding: '3px 10px', borderRadius: 12 }}>
                                        {readyCount} {t('ready')}
                                    </span>
                                    {reviewCount > 0 && (
                                        <span style={{ fontSize: 12, fontWeight: 600, color: C.amber, background: C.amberLight, padding: '3px 10px', borderRadius: 12 }}>
                                            {reviewCount} {t('need_review')}
                                        </span>
                                    )}
                                    {errorCount > 0 && (
                                        <span style={{ fontSize: 12, fontWeight: 600, color: C.red, background: C.redLight, padding: '3px 10px', borderRadius: 12 }}>
                                            {errorCount} {t('errors')}
                                        </span>
                                    )}
                                    <div style={{ flex: 1 }} />
                                    <button onClick={() => { setCsvRows([]); setCsvChecked({}); }}
                                        style={{ padding: '5px 12px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.white, color: C.muted, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                        {t('clear')}
                                    </button>
                                </div>

                                {/* Review table */}
                                <div style={{ overflow: 'auto', maxHeight: 380, border: `1px solid ${C.border}`, borderRadius: 8 }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                        <thead>
                                            <tr style={{ background: C.bg, position: 'sticky', top: 0, zIndex: 2 }}>
                                                <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: `1px solid ${C.border}`, width: 36 }}>
                                                    <input type="checkbox"
                                                        checked={selectedCount === csvRows.length && csvRows.length > 0}
                                                        onChange={e => {
                                                            const next = {};
                                                            csvRows.forEach((_, i) => { next[i] = e.target.checked; });
                                                            setCsvChecked(next);
                                                        }}
                                                        style={{ accentColor: C.green, cursor: 'pointer' }} />
                                                </th>
                                                <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: `1px solid ${C.border}`, color: C.muted, fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                                    {t('status')}
                                                </th>
                                                {columns.map(col => (
                                                    <th key={col.key} style={{ padding: '8px 10px', textAlign: 'left', borderBottom: `1px solid ${C.border}`, color: C.muted, fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8, whiteSpace: 'nowrap' }}>
                                                        {col.label}{col.required ? ' *' : ''}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {csvValidated.map((v, i) => {
                                                const sc = statusColor(v.status);
                                                return (
                                                    <tr key={i} style={{ background: sc.bg }}>
                                                        <td style={{ padding: '6px 10px', textAlign: 'center', borderBottom: `1px solid ${C.border}` }}>
                                                            <input type="checkbox" checked={!!csvChecked[i]}
                                                                onChange={e => setCsvChecked(prev => ({ ...prev, [i]: e.target.checked }))}
                                                                disabled={v.status === STATUS_ERROR}
                                                                style={{ accentColor: C.green, cursor: v.status === STATUS_ERROR ? 'not-allowed' : 'pointer' }} />
                                                        </td>
                                                        <td style={{ padding: '6px 10px', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>
                                                            <span style={{ fontSize: 11, fontWeight: 600, color: sc.fg }}>
                                                                {v.status === STATUS_READY && t('ready')}
                                                                {v.status === STATUS_ERROR && t('errors')}
                                                                {v.status === STATUS_REVIEW && (
                                                                    <span title={v.fuzzyMatches.map(m => m.name).join(', ')}>
                                                                        {t('similar_to')} "{v.fuzzyMatches[0]?.name}"
                                                                    </span>
                                                                )}
                                                            </span>
                                                        </td>
                                                        {columns.map(col => (
                                                            <td key={col.key} style={{
                                                                padding: '6px 10px', borderBottom: `1px solid ${C.border}`,
                                                                color: v.errors.includes(col.key) ? C.red : C.text,
                                                                fontWeight: v.errors.includes(col.key) ? 600 : 400,
                                                                maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                            }}>
                                                                {v.row[col.key] || '—'}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Import button */}
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                                    <button onClick={handleImportCSV} disabled={importing || selectedCount === 0}
                                        style={{
                                            padding: '9px 24px', borderRadius: 8, border: 'none',
                                            background: importing || selectedCount === 0 ? C.mutedLight : C.green,
                                            color: C.white, fontSize: 13, fontWeight: 600, cursor: importing || selectedCount === 0 ? 'not-allowed' : 'pointer',
                                        }}>
                                        {importing ? t('importing') : `${t('import_selected')} (${selectedCount})`}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* ── Manual Tab ────────────────────────────────────────────────────── */}
                {activeTab === 'manual' && (
                    <div style={{ flex: 1, overflow: 'auto' }}>
                        {exampleRows && exampleRows.length > 0 && (
                            <div style={{ marginBottom: 14, padding: '10px 14px', background: '#eff5ff', border: '1px solid #bbf7d0', borderRadius: 8 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>{t('example') || 'Example'}</div>
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                        <thead>
                                            <tr>
                                                {columns.map(col => (
                                                    <th key={col.key} style={{ padding: '3px 8px', textAlign: 'left', color: '#16a34a', fontWeight: 600, whiteSpace: 'nowrap', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                        {col.label}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {exampleRows.map((row, i) => (
                                                <tr key={i}>
                                                    {columns.map(col => (
                                                        <td key={col.key} style={{ padding: '3px 8px', color: '#15803d', whiteSpace: 'nowrap', fontWeight: 500 }}>
                                                            {row[col.key] || '—'}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                        <div style={{ overflow: 'auto', maxHeight: 420, border: `1px solid ${C.border}`, borderRadius: 8 }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                <thead>
                                    <tr style={{ background: C.bg, position: 'sticky', top: 0, zIndex: 2 }}>
                                        <th style={{ padding: '8px 6px', borderBottom: `1px solid ${C.border}`, width: 32, color: C.muted, fontWeight: 700, fontSize: 11 }}>#</th>
                                        {columns.map(col => (
                                            <th key={col.key} style={{ padding: '8px 8px', textAlign: 'left', borderBottom: `1px solid ${C.border}`, color: C.muted, fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8, whiteSpace: 'nowrap' }}>
                                                {col.label}{col.required ? ' *' : ''}
                                            </th>
                                        ))}
                                        <th style={{ padding: '8px 6px', borderBottom: `1px solid ${C.border}`, width: 32 }} />
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row, idx) => {
                                        const v = manualValidated[idx];
                                        const hasContent = columns.some(c => row[c.key] && String(row[c.key]).trim());
                                        const fuzzy = v?.fuzzyMatches?.length > 0;
                                        return (
                                            <tr key={idx} style={{ background: hasContent && v?.status === STATUS_ERROR ? C.redLight : fuzzy ? C.amberLight : C.white }}>
                                                <td style={{ padding: '4px 6px', borderBottom: `1px solid ${C.border}`, color: C.mutedLight, fontSize: 11, textAlign: 'center' }}>{idx + 1}</td>
                                                {columns.map(col => {
                                                    const isTouched = touchedCells[`${idx}-${col.key}`];
                                                    const isEmpty = col.required && isTouched && !row[col.key] && row[col.key] !== 0;
                                                    const isFuzzyName = col.key === 'name' && fuzzy;

                                                    if (col.type === 'dropdown') {
                                                        return (
                                                            <td key={col.key} style={{ padding: '3px 4px', borderBottom: `1px solid ${C.border}`, minWidth: 100 }}>
                                                                <InlineDropdown
                                                                    value={row[col.key]}
                                                                    options={getOptions(col, row)}
                                                                    onChange={val => updateRow(idx, col.key, val)}
                                                                    allowCreate={col.allowCreate}
                                                                    onCreateOption={col.onCreateOption}
                                                                    placeholder={col.label}
                                                                    t={t}
                                                                    rowData={row}
                                                                />
                                                            </td>
                                                        );
                                                    }

                                                    return (
                                                        <td key={col.key} style={{ padding: '3px 4px', borderBottom: `1px solid ${C.border}`, position: 'relative', minWidth: col.type === 'number' ? 70 : 100 }}>
                                                            <input
                                                                type={col.type === 'number' ? 'number' : 'text'}
                                                                value={row[col.key] ?? ''}
                                                                onChange={e => !col.readOnly && updateRow(idx, col.key, col.type === 'number' ? e.target.value : e.target.value)}
                                                                onBlur={() => markTouched(idx, col.key)}
                                                                placeholder={col.readOnly ? t('auto') || 'Auto' : col.label}
                                                                step={col.type === 'number' ? 'any' : undefined}
                                                                readOnly={col.readOnly}
                                                                tabIndex={col.readOnly ? -1 : undefined}
                                                                style={{
                                                                    width: '100%', padding: '5px 8px', boxSizing: 'border-box',
                                                                    border: `1px solid ${isEmpty ? C.red : C.border}`,
                                                                    borderRadius: 4, fontSize: 12,
                                                                    color: col.readOnly ? C.muted : C.text,
                                                                    background: col.readOnly ? '#f1f5f9' : isEmpty ? C.redLight : C.white,
                                                                    cursor: col.readOnly ? 'not-allowed' : undefined,
                                                                }}
                                                            />
                                                            {isFuzzyName && (
                                                                <div style={{
                                                                    marginTop: 2, padding: '4px 8px', borderRadius: 4,
                                                                    background: C.amberLight, border: `1px solid #fcd34d`,
                                                                    fontSize: 11, color: '#92400e',
                                                                }}>
                                                                    &#9888; {t('did_you_mean')} "<strong>{v.fuzzyMatches[0]?.name}</strong>"? {t('product_already_exists')}. {t('change_name_to_continue')}
                                                                </div>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                                <td style={{ padding: '3px 4px', borderBottom: `1px solid ${C.border}`, textAlign: 'center' }}>
                                                    {rows.length > 1 && (
                                                        <button onClick={() => removeRow(idx)}
                                                            style={{
                                                                width: 22, height: 22, borderRadius: 4, border: 'none',
                                                                background: 'transparent', color: C.mutedLight, cursor: 'pointer', fontSize: 14,
                                                            }}
                                                            onMouseEnter={e => e.currentTarget.style.color = C.red}
                                                            onMouseLeave={e => e.currentTarget.style.color = C.mutedLight}>
                                                            &times;
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Add row + submit */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 14, alignItems: 'center' }}>
                            <button onClick={addRow}
                                style={{
                                    padding: '7px 14px', borderRadius: 7, border: `1px solid ${C.border}`,
                                    background: C.white, color: C.muted, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', gap: 5,
                                }}>
                                <span style={{ fontSize: 15, lineHeight: 1 }}>+</span> {t('add_row')}
                            </button>
                            <button onClick={handleAddManual}
                                disabled={importing || manualValidated.filter(v => v.status === STATUS_READY).length === 0}
                                style={{
                                    padding: '9px 24px', borderRadius: 8, border: 'none',
                                    background: importing || manualValidated.filter(v => v.status === STATUS_READY).length === 0 ? C.mutedLight : C.green,
                                    color: C.white, fontSize: 13, fontWeight: 600,
                                    cursor: importing || manualValidated.filter(v => v.status === STATUS_READY).length === 0 ? 'not-allowed' : 'pointer',
                                }}>
                                {importing ? t('adding') : `${t('add_product')} (${manualValidated.filter(v => v.status === STATUS_READY && columns.some(c => v.row[c.key] && String(v.row[c.key]).trim())).length})`}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BulkAddModal;
